import IconButton from '@material-ui/core/IconButton';
import { withStyles } from '@material-ui/core/styles';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import DoubleArrow from '@material-ui/icons/DoubleArrow';
import FaceOutlinedIcon from '@material-ui/icons/FaceOutlined';
import SendIcon from '@material-ui/icons/Send';
import ThumbDownIcon from '@material-ui/icons/ThumbDown';
import ThumbDownOutlinedIcon from '@material-ui/icons/ThumbDownOutlined';
import ThumbUpIcon from '@material-ui/icons/ThumbUp';
import ThumbUpOutlinedIcon from '@material-ui/icons/ThumbUpOutlined';
import axios from 'axios';
import clsx from 'clsx';
import * as _ from 'lodash';
import { marked } from 'marked';
import * as moment from 'moment';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { withRouter } from 'react-router-dom';
import { getRequest } from 'utils/apiRequests';
import streamingGif from '../../assets/gifs/streaming.gif';
import stopIcon from '../../assets/png/stop-icon.png';
import { aiBaseUrl, baseURL } from '../../utils/apiEndpoints';
import { getCookies, uuid } from '../../utils/common';
import { ROLES, TIMEOUT } from '../../utils/consts';

// Helper function to convert plain URLs to markdown links and add target="_blank" to all links
const processContentWithLinks = (content) => {
  if (!content) return content;

  // First, convert plain URLs to markdown links
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const contentWithMarkdownLinks = content.replace(urlRegex, '[$1]($1)');

  // Parse markdown to HTML
  const html = marked.parse(contentWithMarkdownLinks);

  // Add target="_blank" and rel="noopener noreferrer" to all anchor tags
  return html.replace(
    /<a\s+(?:[^>]*?\s+)?href="([^"]*)"([^>]*)>/gi,
    '<a href="$1" target="_blank" rel="noopener noreferrer"$2>'
  );
};

const PANEL_WIDTH = 504;
const PANEL_MIN_WIDTH = 300;
const PANEL_MAX_WIDTH = 800;
const INPUT_HEIGHT = 48;

class ChatPanel extends Component {
  constructor(props) {
    super(props);
    this.state = {
      input: '',
      chat: [],
      loading: false,
      fetchingMessages: true,
      isMinimized: false,
      restoringMessageId: null,
      rId: null,
      panelWidth: PANEL_WIDTH,
      isResizing: false,
      feedbackCommentMessageId: null,
      feedbackRating: null,
      streamingProgress: {
        previousSteps: [],
        currentStep: '...',
      },
      autoScrollDisabled: false,
      lastScrollTop: 0,
      animatingMessageId: null,
      animatedText: '',
      isAnimating: false,
    };
    this.chatEndRef = React.createRef();
    this.textareaRef = React.createRef();
    this.panelRef = React.createRef();
    this.feedbackCommentBoxRef = React.createRef();
    this.feedbackCommentInputRef = React.createRef();
    this.feedbackCommentSendButtonRef = React.createRef();
    this.chatHistoryRef = React.createRef();
    this.animationTimeoutRef = null;
    this.animationFrameRef = null;
    this.isInitialLoad = true;
  }

  async componentDidMount() {
    await this.loadMessagesAndItinerary();
    // Add scroll event listener to detect scroll up
    // Use setTimeout to ensure the ref is available after render
    setTimeout(() => {
      if (this.chatHistoryRef.current) {
        this.chatHistoryRef.current.addEventListener('scroll', this.handleScroll);
      }
    }, 0);
  }

  componentDidUpdate(prevProps, prevState) {
    const { onLoadingChange } = this.props;
    const {
      loading, feedbackCommentMessageId, chat, animatingMessageId, isAnimating,
    } = this.state;
    if (prevState.loading !== loading && onLoadingChange) {
      onLoadingChange(loading);
    }
    
    // Re-enable auto-scroll when loading becomes false
    if (prevState.loading && !loading) {
      this.setState({ autoScrollDisabled: false });
    }
    
    // Trigger animation for new agent message added via processFinalResponse
    // Don't animate during initial load (page reload)
    if ((chat.length > prevState.chat.length) && !isAnimating && !this.isInitialLoad) {
      const lastMessage = chat[chat.length - 1];
      if (lastMessage && lastMessage.sender === 'agent' && lastMessage.id !== animatingMessageId) {
        // Check if this is a new message (not just an update)
        const wasInPrevChat = prevState.chat.some((msg) => msg.id === lastMessage.id);
        if (!wasInPrevChat) {
          this.startTypeAnimation(lastMessage.id, lastMessage.text);
        }
      }
    }
    
    // Add/remove click outside listener for comment box
    if (feedbackCommentMessageId && !prevState.feedbackCommentMessageId) {
      document.addEventListener('mousedown', this.handleClickOutsideCommentBox);
    } else if (!feedbackCommentMessageId && prevState.feedbackCommentMessageId) {
      document.removeEventListener('mousedown', this.handleClickOutsideCommentBox);
    }
  }

  componentWillUnmount() {
    // Clean up resize event listeners
    document.removeEventListener('mousemove', this.handleResizeMove);
    document.removeEventListener('mouseup', this.handleResizeEnd);
    document.removeEventListener('mousedown', this.handleClickOutsideCommentBox);
    
    // Clean up scroll event listener
    if (this.chatHistoryRef.current) {
      this.chatHistoryRef.current.removeEventListener('scroll', this.handleScroll);
    }
    
    // Clean up animation
    if (this.animationTimeoutRef) {
      clearTimeout(this.animationTimeoutRef);
      this.animationTimeoutRef = null;
    }
    if (this.animationFrameRef) {
      cancelAnimationFrame(this.animationFrameRef);
      this.animationFrameRef = null;
    }
    
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }

  loadMessagesAndItinerary = async () => {
    try {
      const {
        match, updateItineraryData, setFirstMessageAt, location, history,
      } = this.props;
      const { itineraryData } = this.state;
      const itineraryId = match?.params?.itineraryId;
      if (!itineraryId) return;
      const response = await getRequest(`${baseURL}itineraries/${itineraryId}/messages`);
      if (response.error) { 
        throw (new Error(response.message));
      }
      let chat = [];
      if (!_.isEmpty(response.messages)) {
        chat = response.messages;
        setFirstMessageAt(chat[0].ts);
      }
      const newItineraryData = _.isEmpty(response?.itinerary) ? itineraryData : response.itinerary;
      updateItineraryData(newItineraryData);
   
      if (_.isEmpty(chat)) {
        this.sendMessageToAgent('plan the trip');
      } else {
        const rId = response.rId;
        await this.setState({
          chat,
          rId,
          fetchingMessages: false,
          uiCues: response?.uiCues || [],          
        });
        if (_.isEmpty(rId)) { 
          this.handleMessageFromUrl();
        } else {
          try {
            this.setState({
              loading: true,
              streamingProgress: {
                previousSteps: response.progressData?.previousSteps || [],
                currentStep: response.progressData?.currentStep || '...',
              },
            });
        
            const finalResponse = await this.checkProgressUntilComplete(rId);

            this.processFinalResponse(finalResponse);
          } catch (err) {
            // eslint-disable-next-line no-console
            console.error('Error in loadMessagesAndItinerary:', err);

            this.setState({
              streamingProgress: {
                previousSteps: [],
                currentStep: '...',
              },
            });

            const { stopped } = this.state;
            if (!stopped) {
              this.addToChat({
                message: {
                  id: `${Date.now()}-error`,
                  sender: 'system',
                  type: 'text',
                  text: 'I encountered a network error. Kindly reload the page and try again.',
                  timestamp: new Date().toISOString(),
                },
              });
            }
          } finally {
            this.setState({ loading: false });
            this.scrollToBottom();            
          }
        }
      }    
    } catch (error) {
      // Error handled silently
    } finally {
      this.autoScrollToBottom();
      // Mark initial load as complete
      this.isInitialLoad = false;
    }
  }

  getItineraryId = (props = this.props) => {
    const { match } = props;
    return match?.params?.itineraryId || match?.params?.ItineraryId || match?.params?.id;
  };

  // Poll progress and return final response when complete
  checkProgressUntilComplete = async (rId) => {
    if (_.isNil(rId)) {
      return {};
    }
    let finalResponse = null;

    const checkProgress = async () => {
      try {
        const progressResponse = await axios.get(`${aiBaseUrl}itineraries/${this.getItineraryId()}/progress/${rId}`, {
          headers: {
            'Content-Type': 'application/json',
          },
        });
        const progressData = progressResponse?.data?.data;
       
        if (progressData?.type === 'progress') {
          this.setState({
            streamingProgress: {
              previousSteps: progressData.previousSteps,
              currentStep: progressData.currentStep,
            },
          }, this.autoScrollToBottom);
        }

        // If progress is complete, stop polling
        if (progressData?.type === 'final') {
          finalResponse = progressData.response;
        }
      } catch (pollError) {
        // eslint-disable-next-line no-console
        console.log('Progress polling error:', pollError);
        this.setState({
          streamingProgress: {
            previousSteps: [],
            currentStep: '...',
          },
          loading: false,
        });
        finalResponse = {};
      }
    };

    // Wait for polling to complete (when finalResponse is set)
    while (_.isNil(finalResponse)) {
      // eslint-disable-next-line no-await-in-loop
      await checkProgress();
      // eslint-disable-next-line no-await-in-loop
      await new Promise((resolve) => setTimeout(resolve, 5000));
    }

    return finalResponse;
  };

  handleMessageFromUrl = () => {
    const { location, history } = this.props;
    // Check if URL query has a message parameter
    const urlParams = new URLSearchParams(location?.search);
    const messageFromUrl = urlParams.get('message');
    if (!_.isEmpty(messageFromUrl)) {
      this.sendMessageToAgent(messageFromUrl);
      // Remove the message query parameter to prevent duplicate messages on reload
      urlParams.delete('message');
      const newSearch = urlParams.toString();
      history.replace({
        pathname: location.pathname,
        search: newSearch ? `?${newSearch}` : '',
      });
    }
  };

  // Toggle minimize/maximize
  toggleMinimize = () => {
    const { onMinimizeChange } = this.props;
    const { isMinimized } = this.state;
    const newMinimizedState = !isMinimized;
    this.setState({ isMinimized: newMinimizedState });
    if (onMinimizeChange) {
      onMinimizeChange(newMinimizedState);
    }
  };

  // Handle resize start
  handleResizeStart = (e) => {
    e.preventDefault();
    this.setState({ isResizing: true });
    document.addEventListener('mousemove', this.handleResizeMove);
    document.addEventListener('mouseup', this.handleResizeEnd);
    document.body.style.cursor = 'ew-resize';
    document.body.style.userSelect = 'none';
  };

  // Handle resize move
  handleResizeMove = (e) => {
    const { isResizing } = this.state;
    if (!isResizing) return;

    const panelRect = this.panelRef?.current?.getBoundingClientRect();
    if (!panelRect) return;

    const newWidth = e.clientX - panelRect.left;
    const clampedWidth = Math.max(PANEL_MIN_WIDTH, Math.min(PANEL_MAX_WIDTH, newWidth));

    this.setState({ panelWidth: clampedWidth });
  };

  // Handle resize end
  handleResizeEnd = () => {
    this.setState({ isResizing: false });
    document.removeEventListener('mousemove', this.handleResizeMove);
    document.removeEventListener('mouseup', this.handleResizeEnd);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  };

  // Handle scroll events to detect scroll up
  handleScroll = () => {
    const { loading, autoScrollDisabled, isAnimating } = this.state;
    const chatHistoryElement = this.chatHistoryRef.current;
    
    if (!chatHistoryElement) return;
    
    // Don't process scroll events during animation to prevent re-triggering
    if (isAnimating) {
      return;
    }
    
    const currentScrollTop = chatHistoryElement.scrollTop;
    const scrollHeight = chatHistoryElement.scrollHeight;
    const clientHeight = chatHistoryElement.clientHeight;
    const { lastScrollTop } = this.state;
    
    // Detect scroll up (user scrolling up)
    if (loading && currentScrollTop < lastScrollTop) {
      // User scrolled up while loading - disable auto-scroll
      if (!autoScrollDisabled) {
        this.setState({ autoScrollDisabled: true });
      }
    }
    
    // Re-enable auto-scroll if user scrolls back near the bottom
    const isNearBottom = scrollHeight - currentScrollTop - clientHeight < 100;
    if (autoScrollDisabled && isNearBottom) {
      this.setState({ autoScrollDisabled: false });
    }
    
    this.setState({ lastScrollTop: currentScrollTop });
  };

  // Auto-scroll to bottom
  autoScrollToBottom = () => {
    const { autoScrollDisabled } = this.state;
    
    // Don't auto-scroll if disabled
    if (autoScrollDisabled) {
      return;
    }
    
    if (this.chatEndRef.current) {
      // Use a more efficient scroll method to prevent freezing
      setTimeout(() => {
        const { autoScrollDisabled: currentAutoScrollDisabled } = this.state;
        if (this.chatEndRef.current && !currentAutoScrollDisabled) {
          try {
            this.chatEndRef.current.scrollIntoView({
              behavior: 'smooth',
              block: 'end',
              inline: 'nearest',
            });
          } catch (error) {
            // Fallback to instant scroll if smooth scroll fails
            this.chatEndRef.current.scrollIntoView({
              behavior: 'auto',
              block: 'end',
              inline: 'nearest',
            });
          }
        }
      }, 100); // Small delay to ensure content is rendered
    }
  };

  // Helper: restore itinerary to a specific message state
  restoreItinerary = async (messageId, itineraryIdParam) => {
    try {      
      const response = await axios({
        method: 'post',
        url: `${aiBaseUrl}itineraries/${itineraryIdParam}/restore`,
        data: { chatMessageId: messageId },
        headers: {
          'Content-Type': 'application/json',
          'x-access-token': getCookies().token,
        },
        timeout: TIMEOUT,
      });

      this.setState({
        chat: response.data.messages,
        uiCues: response.data.uiCues,
      });
      const { updateItineraryData } = this.props;
      if (response.data.itinerary) {
        updateItineraryData(response.data.itinerary);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error restoring itinerary:', error);
      throw error;
    }
  };
  
  // Helper: add message(s) to chat
  addToChat = ({ message, ...otherStateUpdates }) => {
    const messages = Array.isArray(message) ? message : [message];
    this.setState((prevState) => {
      const combined = [...prevState.chat, ...messages];
      const newChat = _.uniqBy(combined, 'id');
      return { chat: newChat, ...otherStateUpdates };
    });
  };

  // Start type animation for a message
  startTypeAnimation = (messageId, fullText) => {
    // Clean up any existing animation
    if (this.animationTimeoutRef) {
      clearTimeout(this.animationTimeoutRef);
      this.animationTimeoutRef = null;
    }
    if (this.animationFrameRef) {
      cancelAnimationFrame(this.animationFrameRef);
      this.animationFrameRef = null;
    }

    // Initialize animation state
    this.setState({
      animatingMessageId: messageId,
      animatedText: '',
      isAnimating: true,
    });

    let currentIndex = 0;
    const text = fullText || '';
    const charsPerFrame = 1; // Animate 2 characters at a time for smoother effect
    const baseDelay = 50; // Base delay in milliseconds

    const animate = () => {
      if (currentIndex >= text.length) {
        // Animation complete
        this.setState({
          isAnimating: false,
          animatedText: text,
        }, () => {
          // Final scroll to ensure all content is visible
          if (this.chatEndRef.current) {
            setTimeout(() => {
              if (this.chatEndRef.current) {
                this.chatEndRef.current.scrollIntoView({
                  behavior: 'smooth',
                  block: 'end',
                  inline: 'nearest',
                });
              }
            }, 100);
          }
        });
        // Keep animatingMessageId set so we know this message was animated
        return;
      }

      // Get next chunk of text
      let nextIndex = Math.min(currentIndex + charsPerFrame, text.length);
      
      // Try to avoid breaking words - if we're in the middle of a word, continue to the end
      if (nextIndex < text.length && text[nextIndex] !== ' ' && text[nextIndex] !== '\n') {
        // Check if we should continue to end of word for smoother animation
        const nextSpace = text.indexOf(' ', nextIndex);
        const nextNewline = text.indexOf('\n', nextIndex);
        const nextBreak = nextSpace !== -1 && nextNewline !== -1 
          ? Math.min(nextSpace, nextNewline)
          : (nextSpace !== -1 ? nextSpace : (nextNewline !== -1 ? nextNewline : text.length));
        
        // Only extend if it's a short word (less than 10 chars)
        if (nextBreak - currentIndex < 10) {
          nextIndex = nextBreak + 1;
        }
      }

      const newText = text.substring(0, nextIndex);
      
      this.setState({ animatedText: newText }, () => {
        // Scroll to keep content visible during animation
        // Use requestAnimationFrame to ensure smooth scrolling
        this.animationFrameRef = requestAnimationFrame(() => {
          if (this.chatEndRef.current && this.chatHistoryRef.current) {
            const chatHistory = this.chatHistoryRef.current;
            const scrollHeight = chatHistory.scrollHeight;
            const clientHeight = chatHistory.clientHeight;
            const scrollTop = chatHistory.scrollTop;
            
            // Only scroll if we're near the bottom (within 100px)
            const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
            if (isNearBottom) {
              this.chatEndRef.current.scrollIntoView({
                behavior: 'smooth',
                block: 'end',
                inline: 'nearest',
              });
            }
          }
        });
      });

      currentIndex = nextIndex;

      // Calculate delay - slightly faster for spaces/newlines
      const currentChar = text[currentIndex - 1] || '';
      const delay = (currentChar === ' ' || currentChar === '\n') ? baseDelay * 0.7 : baseDelay;

      this.animationTimeoutRef = setTimeout(animate, delay);
    };

    // Start animation after a small delay to ensure DOM is ready
    this.animationTimeoutRef = setTimeout(() => {
      animate();
    }, 50);
  };

  // Helper: process final response from agent
  processFinalResponse = (finalResponse) => {
    const { updateItineraryData } = this.props;
    
    // Process final response
    if (_.isEmpty(finalResponse?.responseText)) {
      throw (new Error());
    }
    if (!_.isEmpty(finalResponse.itinerary?.itineraryParts)) {
      updateItineraryData(finalResponse.itinerary);
    }

    this.addToChat({
      message: {
        id: finalResponse.chatMessageId,
        sender: 'agent',
        type: 'text',
        text: finalResponse.responseText,
        ts: new Date().toISOString(),
        itineraryUpdated: !_.isEmpty(finalResponse.itinerary?.itineraryParts),
        images: finalResponse.images,
      },
      uiCues: finalResponse.uiCues,
      streamingProgress: {
        previousSteps: [],
        currentStep: '...',
      },
    });
  };
  
  // Helper: send message to agent API with streaming support
  sendMessageToAgent = async (message) => {
    this.setState({ loading: true });
    this.autoScrollToBottom();
    const { updateItineraryData, setFirstMessageAt } = this.props;
    let { rId } = this.state;
    const { chat } = this.state;

    // Set firstMessageAt if chat is empty
    if (chat.length === 0 && setFirstMessageAt) {
      setFirstMessageAt(new Date().toISOString());
    }

    try {
      this.addToChat({
        message: {
          id: `${Date.now()}-user`,
          sender: 'user',
          type: 'text',
          text: message,
          ts: new Date().toISOString(),
        },
        fetchingMessages: false,
        streamingProgress: {
          previousSteps: [],
          currentStep: '...',
        },
      });

      let finalResponse = null;
      try {
        const response = await fetch(`${aiBaseUrl}itineraries/${this.getItineraryId()}/startAgent`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-access-token': getCookies().token,
          },
          body: JSON.stringify({
            message,
            itineraryId: this.getItineraryId(),
            mode: 'langgraph',
            stream: true,
          }),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        // Process streaming chunks
        // eslint-disable-next-line no-constant-condition
        while (true) {
          // eslint-disable-next-line no-await-in-loop
          const { done, value } = await reader.read();
        
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n\n');
          buffer = lines.pop() || ''; // Keep incomplete line in buffer

          for (const line of lines) {
            if (line.trim() === '') continue;
            try {
              const data = JSON.parse(line);
            
              if (data.type === 'progress') {
                rId = rId || data.rId;
                // Capture rId value for this iteration to avoid closure issues
                const currentRId = rId;
                this.setState((prevState) => {
                  const { chat: prevChat } = prevState;
                  const updatedChat = [...prevChat];
                  if (updatedChat.length > 0 && currentRId) {
                    const lastMessage = updatedChat[updatedChat.length - 1];
                    updatedChat[updatedChat.length - 1] = { ...lastMessage, rId: currentRId };
                  }
                  return {
                    chat: updatedChat,
                    streamingProgress: {
                      previousSteps: data.previousSteps,
                      currentStep: data.currentStep,
                    },
                  };
                }, this.autoScrollToBottom);
              }
            
              // Check if this is the final response
              if (data.type === 'final') {
                finalResponse = data.response || data;
              }
            } catch (jsonError) {
              // Failed to parse streaming JSON - continue processing
            }
          }
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Streaming Error:', error);
        finalResponse = await this.checkProgressUntilComplete(rId);
      }
      
      this.processFinalResponse(finalResponse);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error in sendMessageToAgent:', err);

      this.setState({
        streamingProgress: {
          previousSteps: [],
          currentStep: '...',
        },
      });

      const { stopped } = this.state;
      if (!stopped) {
        this.addToChat({
          message: {
            id: `${Date.now()}-error`,
            sender: 'system',
            type: 'text',
            text: 'I encountered a network error. Kindly reload the page and try again.',
            timestamp: new Date().toISOString(),
          },
        });
      }
    } finally {
      this.setState({ loading: false });
      this.autoScrollToBottom();
    }
  };

  // Handle send
  handleSend = () => {
    const { input, loading } = this.state;
    if (!input.trim() || loading) return;
    this.sendMessageToAgent(input.trim());
    this.setState({ input: '', stopped: false });
    if (this.textareaRef.current) {
      this.textareaRef.current.style.height = `${INPUT_HEIGHT}px`;
    }
  };

  handleStop = async () => {
    const { loading } = this.state;
    if (!loading) return;
    await fetch(`${aiBaseUrl}itineraries/${this.getItineraryId()}/stopAgent`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-access-token': getCookies().token,
      },
    });
    await this.setState({
      loading: false,
      stopped: true,
      streamingProgress: {
        previousSteps: [],
        currentStep: '...',
      },
    });
  };

  // Handle UI cue click
  handleCueClick = (cueText) => {
    const { loading } = this.state;
    if (loading) return;
    // this.setState({ input: cueText }, () => {
    //   // Adjust textarea height after setting the input
    //   if (this.textareaRef.current) {
    //     // this.textareaRef.current.style.height = 'auto';
    //     this.textareaRef.current.style.height = `${this.textareaRef.current.scrollHeight}px`;
    //     this.textareaRef.current.focus();
    //   }
    // });

    this.sendMessageToAgent(cueText);
  };

  // Handle restore itinerary to specific message
  handleRestore = async (messageId) => {
    const itineraryId = this.getItineraryId();
    if (!itineraryId || !messageId) return;
    
    this.setState({ restoringMessageId: messageId });
    try {
      await this.restoreItinerary(messageId, itineraryId);
      // Reload the chat to get the updated state
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to restore itinerary:', error);
    } finally {
      this.setState({ restoringMessageId: null });
    }
  };

  // Submit feedback to API
  submitFeedbackToAPI = async (chatMessageId, rating, comment = null) => {
    const itineraryId = this.getItineraryId();
    if (!itineraryId || !chatMessageId) return null;

    const commentToSend = comment ? comment.trim() : '';
    const feedbackData = commentToSend 
      ? { rating, comment: commentToSend }
      : { rating };

    try {
      await axios({
        method: 'post',
        url: `${aiBaseUrl}itineraries/${itineraryId}/${chatMessageId}/feedback`,
        data: feedbackData,
        headers: {
          'Content-Type': 'application/json',
          'x-access-token': getCookies().token,
        },
        timeout: TIMEOUT,
      });

      // Update the chat state to reflect the feedback change
      this.setState((prevState) => ({
        chat: prevState.chat.map((msg) => {
          if (msg.id === chatMessageId) {
            return { ...msg, feedback: feedbackData };
          }
          return msg;
        }),
      }));

      return feedbackData;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error submitting feedback:', error);
      return null;
    }
  };

  // Handle feedback submission
  handleFeedback = async (chatMessageId, rating, comment = null) => {
    const itineraryId = this.getItineraryId();
    if (!itineraryId || !chatMessageId) return;

    const { feedbackCommentMessageId } = this.state;
    
    // If comment box is already open for this message, submit feedback with comment
    if (feedbackCommentMessageId === chatMessageId) {
      // Read comment from ref if not provided (uncontrolled component)
      const commentToSend = comment !== null ? comment.trim() : (this.feedbackCommentInputRef.current?.value || '').trim();
      
      // Update the chat state immediately with the new rating and comment
      const feedbackDataToUpdate = commentToSend 
        ? { rating, comment: commentToSend }
        : { rating };
      
      this.setState((prevState) => ({
        chat: prevState.chat.map((msg) => {
          if (msg.id === chatMessageId) {
            return { ...msg, feedback: feedbackDataToUpdate };
          }
          return msg;
        }),
      }));
      
      const feedbackData = await this.submitFeedbackToAPI(chatMessageId, rating, commentToSend);
      
      if (feedbackData) {
        // Close comment box
        this.setState({
          feedbackCommentMessageId: null,
          feedbackRating: null,
        });
        
        // Clear the input ref
        if (this.feedbackCommentInputRef.current) {
          this.feedbackCommentInputRef.current.value = '';
        }
      }
      return;
    }
    
    // Update the chat state immediately with the new rating (before opening comment box)
    this.setState((prevState) => ({
      chat: prevState.chat.map((msg) => {
        if (msg.id === chatMessageId) {
          return { ...msg, feedback: { rating } };
        }
        return msg;
      }),

      // Open comment box so user can optionally add a comment
      feedbackCommentMessageId: chatMessageId,
      feedbackRating: rating,
    }));
    
    // Clear the input ref when opening comment box
    if (this.feedbackCommentInputRef.current) {
      this.feedbackCommentInputRef.current.value = '';
    }

    // Always submit feedback immediately (user might be changing from thumbs up to thumbs down or vice versa)
    this.submitFeedbackToAPI(chatMessageId, rating);    
  };

  // Handle sending feedback comment
  handleSendFeedbackComment = () => {
    const { feedbackCommentMessageId, feedbackRating } = this.state;
    if (!feedbackCommentMessageId || feedbackRating === null) return;
    
    // Read comment from ref (uncontrolled component)
    const feedbackComment = this.feedbackCommentInputRef.current?.value || '';
    
    // Don't submit if comment is empty
    if (!feedbackComment.trim()) return;
    
    // Update existing feedback with comment
    this.handleFeedback(feedbackCommentMessageId, feedbackRating, feedbackComment);
  };

  // Handle click outside comment box
  handleClickOutsideCommentBox = (event) => {
    const { feedbackCommentMessageId } = this.state;
    if (!feedbackCommentMessageId) return;

    // Check if click is inside the comment box
    if (
      this.feedbackCommentBoxRef.current
      && this.feedbackCommentBoxRef.current.contains(event.target)
    ) {
      return;
    }

    // Check if click is on thumb icons - let handleFeedback handle it
    const target = event.target;
    const isThumbIcon = target.closest('svg')
      && this.feedbackFooterRef
      && Array.from(this.feedbackFooterRef.children).some((child) => (
        child.contains(target) && (child.querySelector('svg') || child.tagName === 'svg')
      ));

    if (isThumbIcon) {
      // Let handleFeedback handle closing the comment box
      return;
    }

    // Close comment box if click is outside
    this.setState({
      feedbackCommentMessageId: null,
      feedbackRating: null,
    });
    
    // Clear the input ref
    if (this.feedbackCommentInputRef.current) {
      this.feedbackCommentInputRef.current.value = '';
    }
  };

  // Render UI cues
  renderUICues = () => {
    const { classes } = this.props;
    const { loading, uiCues } = this.state;
    
    if (_.isEmpty(uiCues) || loading) {
      return null;
    }

    return (
      <div className={classes.uiCuesContainer}>
        {uiCues.map((cue) => (
          <button
            key={cue}
            type="button"
            onClick={() => this.handleCueClick(cue)}
            disabled={loading}
            className={clsx(classes.uiCueButton, loading ? classes.uiCueButtonDisabled : null)}
          >            
            <span className={classes.uiCueText}>{cue}</span>
          </button>
        ))}
      </div>
    );
  };

  // Render streaming message component
  renderStreamingMessage = () => {
    const { classes } = this.props;
    const { streamingProgress } = this.state;
    
    return (
      <div className={classes.streamingMessage}>
        <div className={classes.agentMessageHeader}>
          <FaceOutlinedIcon className={classes.faceIcon} />
          Flynote AI
        </div>
        {!_.isEmpty(streamingProgress.previousSteps) && (
          <div className={classes.stepsContainer}>
            {streamingProgress.previousSteps.map((step, index) => (
              <div
                key={`streaming-prev-step-${uuid()}`}
                className={classes.stepItem}
              >                  
                <CheckCircleIcon className={classes.stepIconImage} />                    
                {step}
              </div>
            ))}
          </div>
        )}
        <div className={classes.streamingIndicator}>
          <img src={streamingGif} alt="loading" className={classes.streamingGifImage} />
          <span className={classes.streamingText}>{streamingProgress.currentStep}</span>
        </div>
      </div>
    );
  };

  // Render chat blocks
  renderChat = () => {
    const { onViewGallery, classes } = this.props;
    const {
      chat, restoringMessageId, feedbackCommentMessageId, animatingMessageId, animatedText, isAnimating,
    } = this.state;

    const AgentMessage = ({ msg, msgIndex }) => {
      // Use animated text if this message is currently being animated or was animated
      const isAnimatedMessage = animatingMessageId === msg.id;
      const isCurrentlyAnimating = isAnimating && animatingMessageId === msg.id;
      const displayText = isAnimatedMessage && animatedText ? animatedText : (msg.text || '');
      
      // Append cursor to HTML if animating
      let htmlContent = processContentWithLinks(displayText);
      if (isCurrentlyAnimating) {
        const cursorSpan = `<span class="${classes.typingCursor}" style="display: inline !important; margin-left: 2px; color: #32302C; font-size: 14px; font-weight: bold; vertical-align: baseline; line-height: inherit;">|</span>`;
        
        // Insert cursor before the last closing tag to keep it inline with the content
        // This handles cases where markdown creates block elements like <p>text</p>
        const lastClosingTagIndex = htmlContent.lastIndexOf('</');
        if (lastClosingTagIndex !== -1) {
          // Insert before the last closing tag (e.g., before </p>)
          htmlContent = htmlContent.slice(0, lastClosingTagIndex) + cursorSpan + htmlContent.slice(lastClosingTagIndex);
        } else {
          // No closing tags found (plain text or self-closing tags), append at the end
          htmlContent += cursorSpan;
        }
      }
      
      return (
        <div className={classes.agentMessageWrapper}>
          <div className={classes.agentMessage}>
            <div className={classes.agentMessageHeader}>
              <FaceOutlinedIcon className={classes.faceIcon} />
              Flynote AI
            </div>
            <div
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{ __html: htmlContent }}
              className={classes.agentMessageContent}
            />
            {
              !isCurrentlyAnimating && !_.isEmpty(msg.images) && (
              <div className={classes.imagesGrid}>
                {msg.images.slice(0, 10).map((imageUrl, index) => (
                  <div
                    key={`${msg.id}-img-${imageUrl}`}
                    role="button"
                    tabIndex={0}
                    onClick={() => {
                      if (onViewGallery) {
                        onViewGallery(msg.images, index);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        if (onViewGallery) {
                          onViewGallery(msg.images, index);
                        }
                      }
                    }}
                    className={classes.imageItem}
                  >
                    <img
                      src={imageUrl}
                      alt={`${index + 1}`}
                      className={classes.imageItemImg}
                    />
                  </div>
                ))}
              </div>
              )
            }
          </div>
      
          {!isCurrentlyAnimating && (
            <div className={classes.agentMessageFooter}>
              <div ref={(el) => { if (feedbackCommentMessageId === msg.id) this.feedbackFooterRef = el; }} className={classes.agentMessageFooterLeft}>
                {msg.feedback?.rating === 1 ? (
                  <ThumbUpIcon 
                    className={clsx(classes.thumbUpIcon, classes.thumbUpIconActive)} 
                    onClick={() => this.handleFeedback(msg.id, 1)}
                  />
                ) : (
                  <ThumbUpOutlinedIcon 
                    className={classes.thumbUpIcon} 
                    onClick={() => this.handleFeedback(msg.id, 1)}
                  />
                )}
                {msg.feedback?.rating === 0 ? (
                  <ThumbDownIcon 
                    className={clsx(classes.thumbDownIcon, classes.thumbDownIconActive)} 
                    onClick={() => this.handleFeedback(msg.id, 0)}
                  />
                ) : (
                  <ThumbDownOutlinedIcon 
                    className={classes.thumbDownIcon} 
                    onClick={() => this.handleFeedback(msg.id, 0)}
                  />
                )}
                {feedbackCommentMessageId === msg.id && (
                  <div ref={this.feedbackCommentBoxRef} className={classes.feedbackCommentBox}>
                    <input
                      ref={this.feedbackCommentInputRef}
                      type="text"
                      defaultValue=""
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          this.handleSendFeedbackComment();
                        }
                      }}
                      placeholder="Add comment (optional)"
                      className={classes.feedbackCommentInput}
                    />
                    <IconButton
                      ref={this.feedbackCommentSendButtonRef}
                      size="small"
                      onClick={this.handleSendFeedbackComment}
                      className={classes.feedbackCommentSendButton}
                    >
                      <SendIcon className={classes.feedbackCommentSendIcon} />
                    </IconButton>
                  </div>
                )}
                {
                  msg.itineraryUpdated
                  && (
                    <>
                      <span className={classes.agentMessageSeparator}>•</span>
                      <span>Itinerary Updated</span>                  
                    </>
                  )
                }
                {
                  (msgIndex < chat.length - 1)
                  && (
                    <>
                      <span className={classes.agentMessageSeparator}>•</span>
                      <button
                        type="button"
                        onClick={() => this.handleRestore(msg.id)}
                        disabled={restoringMessageId === msg.id}
                        className={`${classes.restoreButton} ${restoringMessageId === msg.id ? classes.restoreButtonDisabled : classes.restoreButtonEnabled}`}                    
                      >
                        {restoringMessageId === msg.id ? 'Restoring...' : 'Restore'}
                      </button>
                    </>
                  )
                }            
                {
                  msg.ts && (
                    <>
                      <span className={classes.agentMessageSeparator}>•</span>
                      <div className={classes.timestamp}>
                        {moment(msg.ts).format('Do MMM HH:mm')}
                      </div>
                    </>
                  )
                }
              </div>
            </div>
          )}
        </div>
      );
    };

    const UserMessage = ({ msg }) => (
      <div
        className={`${classes.messageBubbleWrapper} ${msg.sender === 'user' ? classes.messageBubbleWrapperUser : classes.messageBubbleWrapperAgent}`}
      >
        {msg.senderName && (
          <div
            className={`${classes.senderName} ${msg.sender === 'user' ? classes.senderNameRight : classes.senderNameLeft}`}
          >
            {msg.senderName}
          </div>
        )}
        <div
          className={`${classes.messageBubble} ${msg.sender === 'user' ? classes.messageBubbleUser : classes.messageBubbleAgent}`}
        >
          {msg.text}
        </div>
        {
          msg.ts && (
            <div
              className={classes.messageTimestamp}
              role="button"
              tabIndex={msg.rId ? 0 : -1}
              onClick={() => {
                if (!msg.rId) return;
                const { expert } = this.props;
                if (!expert?.roles?.includes(ROLES.ADMIN)) return;
                const projectId = process.env.REACT_APP_ENV === 'production' 
                  ? '220d7403-5386-4e97-9652-c60a063a6cb4' 
                  : '36f1937f-c479-40f9-b317-510c0ce65305';
                const url = `https://smith.langchain.com/o/fb764d68-b2fa-4188-9967-f8a7f9e31dfa/projects/p/${projectId}?timeModel=%7B%22duration%22%3A%227d%22%7D&searchModel=%7B%22filter%22%3A%22and%28eq%28is_root%2C+true%29%2C+eq%28id%2C+%5C%22${msg.rId}%5C%22%29%29%22%2C%22searchFilter%22%3A%22eq%28is_root%2C+true%29%22%7D`;
                window.open(url, '_blank', 'noopener,noreferrer');
              }}
              style={{ cursor: msg.rId ? 'pointer' : 'default' }}
            >
              {moment(msg.ts).format('Do MMM YYYY HH:mm')}
            </div>
          )
        }
      </div>
    );
    
    return chat.map((msg, msgIndex) => {
      if (msg.sender === 'agent') {
        return (
          <AgentMessage
            key={msg.id}
            msg={msg}
            msgIndex={msgIndex}
          />
        );
      }
      return <UserMessage key={msg.id} msg={msg} />;
    });
  };

  render() {
    const {
      isMinimized,
      input,
      loading,
      fetchingMessages,
      panelWidth,
      isResizing,
    } = this.state;
    const { classes } = this.props;

    // If minimized, show sidebar
    if (isMinimized) {
      return (
        <div
          role="button"
          tabIndex={0}
          onClick={this.toggleMinimize}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              this.toggleMinimize();
            }
          }}
          className={classes.minimizedSidebar}
        >
          Flynote AI
        </div>
      );
    }

    return (
      <div ref={this.panelRef} className={classes.panelContainer} style={{ width: panelWidth }}>
        {/* Resize Handle */}
        <div
          className={classes.resizeHandle}
          onMouseDown={this.handleResizeStart}
          role="button"
          tabIndex={0}
          aria-label="Resize chat panel"
          style={{
            cursor: isResizing ? 'ew-resize' : 'ew-resize',
          }}
        />

        {/* Header - Fixed */}
        <div className={classes.header}>
          <span className={classes.headerTitle}>
            <FaceOutlinedIcon className={classes.faceIcon} />
            Flynote AI
          </span>
          <div className={classes.headerSpacer} />
          <IconButton
            size="small"
            className={classes.headerButton}
            onClick={this.toggleMinimize}
          >
            <DoubleArrow className={classes.headerButtonIcon} />
          </IconButton>
        </div>
      
        {/* Chat History - Scrollable area */}
        <div ref={this.chatHistoryRef} className={classes.chatHistory}>
          {this.renderChat()}
          {loading && this.renderStreamingMessage()}
          <div ref={this.chatEndRef} />
        </div>

        {/* Input Bar - Fixed at bottom */}
        {
          !fetchingMessages && (        
            <div className={classes.inputBarContainer}>
              {this.renderUICues()}
              <div className={clsx(classes.inputBar, loading ? classes.inputBarLocked : null)}>
                {/* <IconButton className={classes.inputBarAddButton}>
              <span className={classes.inputBarAddButtonText}>+</span>
            </IconButton> */}
                <textarea
                  ref={this.textareaRef}
                  value={input}
                  disabled={loading}
                  onChange={(e) => {
                    this.setState({ input: e.target.value });
                    // Auto-resize textarea
                    e.target.style.height = `${_.isEmpty(e.target.value) ? INPUT_HEIGHT : e.target.scrollHeight}px`;
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      this.handleSend();
                    }
                  }}
                  placeholder={loading ? '' : 'Ask anything — plan trips, craft stories, or win clients'}
                  className={clsx(classes.input, loading ? classes.inputLocked : null)}
                  rows={1}
                />
          
                {
                  loading
                    ? (
                      <IconButton onClick={this.handleStop} className={classes.inputBarSendButton}>
                        <img src={stopIcon} alt="stop" className={classes.stopIconImage} />
                      </IconButton>
                    ) : (
                      <IconButton onClick={this.handleSend} disabled={loading} className={classes.inputBarSendButton}>
                        <SendIcon className={classes.inputBarSendButtonIcon} />
                      </IconButton>
                    )
                }
              </div>
            </div>
          )
        } 
      </div>
    );
  }
}

const styles = (theme) => ({
  streamingMessage: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    borderRadius: 8,
    margin: '16px 0',
    color: '#0090B5',
    fontSize: 14,
    lineHeight: 1.5,
    fontWeight: 500,
  },
  streamingIndicator: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',    
  },
  streamingIndicatorInner: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    background: '#fff',
  },
  streamingGifImage: {
    width: 64,
    height: 64,
    objectFit: 'contain',
  },
  streamingText: {
    flex: 1,
    fontSize: 18,
  },
  stepsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '5px',
    marginLeft: 25,
    position: 'relative',
  },
  stepItem: {
    display: 'flex',
    alignItems: 'center',
    fontSize: 14,
    lineHeight: 1.5,
    color: theme.colors.textLight,
    marginBottom: 4,
    position: 'relative',
    '&:not(:last-child)::after': {
      content: '""',
      position: 'absolute',
      left: 6,
      top: 17,
      height: 17,
      width: 4,
      backgroundColor: 'rgba(0, 144, 181, 0.4)',
    },
  },
  stepIcon: {
    width: 20,
    height: 20,
    borderRadius: '50%',
    background: '#DDDDDD',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 8,
    zIndex: 10,
  },
  stepIconImage: {
    width: 16,
    height: 16,
    marginRight: 10,
    color: 'rgba(0, 144, 181, 0.4)',
  },
  imagesContainer: {
    alignSelf: 'flex-start',
    background: '#fff',
    borderRadius: 12,
    padding: '12px',
    margin: '16px 0 0 0',
    width: 'calc(100% - 48px)',
    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
  },
  imagesGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    marginTop: '15px',
  },
  imageItem: {
    width: 80,
    height: 80,
    borderRadius: 8,
    overflow: 'hidden',
    cursor: 'pointer',
    border: '1px solid #eee',
  },
  imageItemImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  agentMessageWrapper: {
    margin: '0 0 16px 0',    
  },
  agentMessage: {
    alignSelf: 'flex-start',
    color: theme.colors.textDark,
    fontSize: 14,
    // lineHeight: 1.5,    
    wordWrap: 'break-word',
    overflowWrap: 'break-word',
    // whiteSpace: 'pre',
  },
  agentMessageHeader: {
    fontSize: 14,
    color: theme.colors.textLight,
    fontWeight: 500,
    marginBottom: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
  },
  faceIcon: {
    width: 16,
    height: 16,
    marginRight: 5,
    marginTop: 2,
  },
  agentMessageContent: {
    // wordWrap: 'break-word',
    // overflowWrap: 'break-word',
    // whiteSpace: 'pre-wrap',
    '& > span': {
      display: 'inline',
    },
  },
  typingCursor: {
    display: 'inline',
    marginLeft: 2,
    color: theme.colors.textDark,
    fontSize: 14,
    fontWeight: 'bold',
    animation: '$blink 1s infinite',
    verticalAlign: 'baseline',
    lineHeight: 'inherit',
  },
  '@keyframes blink': {
    '0%, 100%': { opacity: 1 },
    '50%': { opacity: 0 },
  },
  agentMessageFooter: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    color: '#32302C',
    fontSize: 14,
    fontFamily: 'Sofia-pro, sans-serif',
    fontWeight: 'bold',
    width: 'calc(100% - 48px)',
    padding: 0,
    marginTop: 5,
  },
  agentMessageFooterLeft: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    gap: '5px',
    position: 'relative',
  },
  thumbUpIcon: {
    marginTop: 5,
    fontSize: 16,
    cursor: 'pointer',
    color: theme.colors.textLight,
  },
  thumbUpIconActive: {
    color: theme.colors.primary,
  },
  thumbDownIcon: {
    marginTop: 5,
    fontSize: 16,
    cursor: 'pointer',
    color: theme.colors.textLight,
  },
  thumbDownIconActive: {
    color: theme.colors.primary,
  },
  feedbackCommentBox: {
    position: 'absolute',
    bottom: '100%',
    left: 0,
    marginBottom: 8,
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    background: theme.colors.white,
    border: `1px solid ${theme.colors.border}`,
    borderRadius: 20,
    padding: '4px 8px',
    minWidth: 200,
    maxWidth: 300,
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    zIndex: 10,
  },
  feedbackCommentInput: {
    flex: 1,
    border: 'none',
    outline: 'none',
    background: 'transparent',
    fontSize: 12,
    fontFamily: 'Sofia-pro, sans-serif',
    padding: '4px 8px',
    color: theme.colors.textDark,
    '&::placeholder': {
      color: theme.colors.textLight,
      opacity: 0.6,
    },
  },
  feedbackCommentSendButton: {
    padding: 4,
    minWidth: 24,
    width: 24,
    height: 24,
    cursor: 'pointer',
  },
  feedbackCommentSendIcon: {
    fontSize: 16,
    color: theme.colors.primary,
  },
  agentMessageSeparator: {
    color: theme.colors.textLight,
  },
  restoreButton: {
    background: 'none',
    color: '#0090B5',
    borderRadius: 4,
    padding: 0,
    fontFamily: 'Sofia-pro, sans-serif',
    fontWeight: 500,
    transition: 'all 0.2s ease',
    textDecoration: 'underline',
  },
  restoreButtonDisabled: {
    cursor: 'not-allowed',
    opacity: 0.6,
  },
  restoreButtonEnabled: {
    cursor: 'pointer',
  },
  timestamp: {
    fontSize: 14,
    color: theme.colors.textLight,
    fontWeight: 500,
    textAlign: 'right',
  },
  messageBubbleWrapper: {
    position: 'relative',    
    marginBottom: 16,
  },
  messageBubbleWrapperUser: {
    alignSelf: 'flex-end',
    maxWidth: '80%',
  },
  messageBubbleWrapperAgent: {
    alignSelf: 'flex-start',
    maxWidth: '80%',
  },
  senderName: {
    fontSize: 8,
    color: '#666',
    fontFamily: 'Sofia-pro, sans-serif',
    fontWeight: 500,
    marginBottom: 4,
    marginRight: 5,
  },
  senderNameRight: {
    textAlign: 'right',
  },
  senderNameLeft: {
    textAlign: 'left',
  },
  messageBubble: {
    borderRadius: 12,    
    padding: '12px 16px',
    fontSize: 14,
    lineHeight: 1.5,
    fontFamily: 'Sofia-pro, sans-serif',
    wordWrap: 'break-word',
    overflowWrap: 'break-word',
    whiteSpace: 'pre-wrap',
  },
  messageBubbleUser: {
    background: theme.colors.white,
    color: '#32302C',
    borderColor: theme.colors.border,
    boxShadow: '0 2px 8px rgba(0,144,181,0.08)',
  },
  messageBubbleAgent: {
    background: '#fff',
    color: '#222',
    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
  },
  messageTimestamp: {
    marginTop: 4,
    fontSize: 14,
    color: theme.colors.textLight,    
    fontWeight: 500,
    textAlign: 'right',
  },
  minimizedSidebar: {
    width: 24,
    height: '100%',
    background: 'rgb(238, 232, 248)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    writingMode: 'vertical-rl',
    textOrientation: 'mixed',
    transform: 'rotate(180deg)',
    fontFamily: 'Sofia-pro, sans-serif',
    fontSize: 12,
    fontWeight: 500,
    color: 'rgb(119, 73, 185)',
    boxShadow: '-2px 0 8px rgba(0,0,0,0.1)',
  },
  resizeHandle: {
    position: 'absolute',
    right: 0,
    top: 0,
    bottom: 0,
    width: 4,
    background: theme.colors.primaryLight,
    cursor: 'ew-resize',
    zIndex: 1000,
    '&:hover': {
      background: 'rgba(0, 144, 181, 0.1)',
    },
  },
  panelContainer: {
    width: PANEL_WIDTH,
    height: '100%',
    background: '#F5F5F5',
    boxShadow: '-4px 0 12px rgba(0,0,0,0.15)',
    display: 'flex',
    flexDirection: 'column',
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    overflow: 'hidden',
    position: 'relative',
  },
  header: {
    height: 32,
    background: '#EEE8F8',
    display: 'flex',
    alignItems: 'center',
    borderBottom: '4px solid #7749B9',
    padding: 0,
    fontFamily: 'Sofia-pro, sans-serif',
    fontWeight: 500,
    fontSize: 14,
    color: '#222',
    position: 'relative',
    flexShrink: 0,
  },
  headerTitle: {
    display: 'flex',
    alignItems: 'center',    
    color: 'rgb(119, 73, 185)',
    fontWeight: 700,
    fontSize: 16,
    fontFamily: 'Sofia-pro, sans-serif',
    marginLeft: 24,
  },
  headerSpacer: {
    flex: 1,
  },
  headerButton: {
    padding: 4,
  },
  headerButtonIcon: {
    fontSize: 24,
    transform: 'rotate(180deg)',
    color: '#A084E8',
  },
  chatHistory: {
    flex: 1,
    overflowY: 'auto',
    overflowX: 'hidden',
    padding: '24px 24px 0 24px',
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    willChange: 'scroll-position',
    WebkitOverflowScrolling: 'touch',
  },
  uiCuesContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    padding: '8px 16px 0 16px',
    background: '#F5F5F5',
    alignItems: 'center',
    justifyContent: 'center',
  },
  uiCueButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 16px',
    background: '#FFFFFF',
    border: '1px solid #CCCCCC',
    borderRadius: 20,
    fontSize: 13,
    fontFamily: 'Sofia-pro, sans-serif',
    fontWeight: 500,
    color: '#32302C',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    whiteSpace: 'nowrap',
    '&:hover': {
      background: '#F8F8F8',
      borderColor: '#999999',
      transform: 'translateY(-1px)',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    },
    '&:active': {
      transform: 'translateY(0)',
    },
  },
  uiCueButtonDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
    '&:hover': {
      background: '#FFFFFF',
      borderColor: '#CCCCCC',
      transform: 'none',
      boxShadow: 'none',
    },
  },
  uiCueIcon: {
    fontSize: 16,
    lineHeight: 1,
    display: 'flex',
    alignItems: 'center',
  },
  uiCueText: {
    fontSize: 13,
    lineHeight: 1,
  },
  inputBarContainer: {
    boxShadow: `0 -4px 8px ${theme.colors.primary}33`,
  },
  inputBar: {
    // minHeight: INPUT_HEIGHT,
    background: theme.colors.white,
    borderRadius: 24,
    margin: 16,
    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
    display: 'flex',
    alignItems: 'center',
    border: `1px solid ${theme.colors.primary}`,
    padding: '0 16px',
    flexShrink: 0,
  },
  inputBarAddButton: {
    width: 48,
    height: 48,
    background: '#fff',
    color: '#666',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  inputBarAddButtonText: {
    fontSize: 24,
    paddingBottom: 5,
    paddingLeft: 0,
    fontWeight: 400,
  },
  input: {
    flex: 1,
    border: 'none',
    outline: 'none',
    background: theme.colors.white,
    borderRadius: 12,
    fontSize: 14,
    padding: '12px 16px',
    fontFamily: 'Sofia-pro, sans-serif',
    resize: 'none',
    maxHeight: 120,
    overflow: 'scroll',
    lineHeight: 1.4,
    '&::placeholder': {
      color: theme.colors.primary,
    },
    height: INPUT_HEIGHT,
  },
  inputBarLocked: {
    background: `${theme.colors.textLight}15`, // 15% opacity of primary color
    borderColor: `${theme.colors.border}`,
  },
  inputLocked: {
    background: 'transparent',
  },
  inputBarSendButton: {
    color: theme.colors.primary,
    marginLeft: 8,
  },
  inputBarSendButtonIcon: {
    fontSize: 24,
  },
});

ChatPanel.propTypes = {
  onMinimizeChange: PropTypes.func,
  onViewGallery: PropTypes.func,
  updateItineraryData: PropTypes.func,
  setIsLoading: PropTypes.func,
  onLoadingChange: PropTypes.func,
  setFirstMessageAt: PropTypes.func,
  expert: PropTypes.object,
  match: PropTypes.object.isRequired,
  location: PropTypes.shape({
    search: PropTypes.string,
    pathname: PropTypes.string,
  }),
  history: PropTypes.shape({
    replace: PropTypes.func.isRequired,
  }).isRequired,
  classes: PropTypes.object.isRequired,
};

export default withRouter(withStyles(styles)(ChatPanel)); 
